import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Loader2, CheckCircle, AlertCircle, Clock } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import { useAIJobQueue } from './AIJobStatusProvider';
import { VStack, HStack } from '@/components/core';

interface AIJobStatusIndicatorProps {
  className?: string;
  variant?: 'minimal' | 'detailed';
}

export function AIJobStatusIndicator({
  className,
  variant = 'minimal',
}: AIJobStatusIndicatorProps): React.ReactElement | null {
  const { t } = useTranslation('admin');
  const { activeJob, queuedJobs, completedJobs } = useAIJobQueue();

  // Don't render if no jobs
  if (!activeJob && queuedJobs.length === 0 && completedJobs.length === 0) {
    return null;
  }

  const hasActivity = activeJob || queuedJobs.length > 0;
  const progressPercent = activeJob?.progress.totalImages
    ? (activeJob.progress.currentImage / activeJob.progress.totalImages) * 100
    : 0;

  if (variant === 'minimal') {
    return (
      <Link
        to="/admin/ai-character-bulk-creation"
        className={cn(
          'flex items-center gap-token-sm px-token-sm py-token-sm rounded-token-button transition-colors',
          hasActivity
            ? 'bg-brand-primary/10 hover:bg-brand-primary/20'
            : 'bg-success/10 hover:bg-success/20',
          className
        )}
      >
        {activeJob ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin text-brand-primary" />
            <span className="text-sm font-medium text-brand-primary">
              {t('aiJobStatus.generating')}
            </span>
            <Badge variant="secondary" className="text-xs">
              {Math.round(progressPercent)}%
            </Badge>
          </>
        ) : queuedJobs.length > 0 ? (
          <>
            <Clock className="h-4 w-4 text-warning" />
            <span className="text-sm font-medium text-warning">
              {t('aiJobStatus.queued', { count: queuedJobs.length })}
            </span>
          </>
        ) : completedJobs.length > 0 ? (
          <>
            <CheckCircle className="h-4 w-4 text-success" />
            <span className="text-sm font-medium text-success">
              {t('aiJobStatus.completed')}
            </span>
          </>
        ) : null}
      </Link>
    );
  }

  // Detailed variant
  return (
    <div className={cn('design-card p-token-md gap-token-sm', className)}>
      <HStack justify="between" align="center">
        <h4 className="text-sm font-semibold design-text-primary">
          {t('aiJobStatus.title')}
        </h4>
        {hasActivity && (
          <Badge variant={activeJob ? 'default' : 'secondary'}>
            {activeJob ? t('aiJobStatus.active') : t('aiJobStatus.waiting')}
          </Badge>
        )}
      </HStack>

      {activeJob && (
        <VStack gap="md">
          <HStack gap="sm" align="center" className="text-sm">
            <Loader2 className="h-4 w-4 animate-spin text-brand-primary" />
            <span className="design-text-secondary">
              {activeJob.characterName || t('aiJobStatus.bulkCreation')}
            </span>
          </HStack>
          <Progress value={progressPercent} className="h-2" />
          <HStack justify="between" className="text-xs text-muted-foreground">
            <span>
              {activeJob.progress.currentImage}/{activeJob.progress.totalImages} {t('aiJobStatus.images')}
            </span>
            <span>{Math.round(progressPercent)}%</span>
          </HStack>
        </VStack>
      )}

      {queuedJobs.length > 0 && (
        <HStack gap="sm" align="center" className="text-sm">
          <Clock className="h-4 w-4 text-warning" />
          <span className="design-text-secondary">
            {t('aiJobStatus.inQueue', { count: queuedJobs.length })}
          </span>
        </HStack>
      )}

      {completedJobs.length > 0 && !hasActivity && (
        <VStack gap="md">
          {completedJobs.slice(0, 3).map((job) => (
            <HStack key={job.id} gap="sm" align="center" className="text-sm">
              {job.state === 'completed' ? (
                <CheckCircle className="h-4 w-4 text-success" />
              ) : (
                <AlertCircle className="h-4 w-4 text-error" />
              )}
              <span className="design-text-secondary truncate">
                {job.characterName || t('aiJobStatus.bulkCreation')}
              </span>
              <span className="text-xs text-muted-foreground ml-auto">
                {job.progress.currentImage}/{job.progress.totalImages}
              </span>
            </HStack>
          ))}
        </VStack>
      )}

      <Link
        to="/admin/ai-character-bulk-creation"
        className="block text-xs text-brand-primary hover:underline mt-token-sm"
      >
        {t('aiJobStatus.viewAll')}
      </Link>
    </div>
  );
}
